import React, { useState, useEffect } from 'react';
import { Presentation as PresentationIcon, Copy, ExternalLink, Trash2 } from 'lucide-react';
import { Showcase } from '../../types';
import { toast } from 'sonner';

interface PortfoliosListProps {
  onNew: () => void;
  showcases: Showcase[];
  onDelete: (id: string) => void;
  onView: (slug: string) => void;
}

export const PortfoliosList: React.FC<PortfoliosListProps> = ({ onNew, showcases, onDelete, onView }) => {
  const copyLink = (slug: string) => {
    const url = `${window.location.origin}/#/showcase/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado! PRonto para enviar ao cliente.');
  };

  const openLink = (slug: string) => {
    const url = `${window.location.origin}/#/showcase/${slug}`;
    window.open(url, '_blank');
  };

  if (showcases.length === 0) {
    return (
      <div className="w-full max-w-5xl mx-auto py-12 px-8 flex flex-col items-center justify-center min-h-[80vh]">
         <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <PresentationIcon size={32} className="text-gray-400" />
         </div>
         <h2 className="text-2xl font-bold text-gray-900 mb-2">Nenhum Portfólio Criado</h2>
         <p className="text-gray-500 text-center max-w-md text-sm mb-8 leading-relaxed">
           Sua estante de vitrines está vazia. Use sua biblioteca para agrupar e criar links mágicos focados em conversão.
         </p>
         <button onClick={onNew} className="bg-black text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-all shadow-md">
            + Nova Vitrine de Vendas
         </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto py-10 px-8">
       <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Meus Portfólios</h2>
            <p className="text-sm text-gray-500 mt-1">Links gerados prontos para prospecção.</p>
          </div>
          <button onClick={onNew} className="bg-black text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-all shadow-sm">
            + Criar Novo
          </button>
       </div>

       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {showcases.map(showcase => (
             <div key={showcase.id} onClick={() => onView(showcase.slug)} className="bg-white border border-gray-200 cursor-pointer rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-gray-300 transition-all flex flex-col group overflow-hidden relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400">
                     <PresentationIcon size={20} />
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); onDelete(showcase.id); }} className="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 p-1 relative z-10">
                     <Trash2 size={16} />
                  </button>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1 leading-tight group-hover:text-indigo-600 transition-colors">{showcase.name}</h3>
                <p className="text-xs text-gray-500 mb-6 font-medium bg-gray-50 w-max px-2 py-1 rounded">
                   {showcase.items.length} itens inclusos
                </p>
                <div className="flex gap-2 mt-auto pt-4 border-t border-gray-50 relative z-10">
                   <button onClick={(e) => { e.stopPropagation(); copyLink(showcase.slug); }} className="flex-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-colors">
                      <Copy size={14} /> Copiar Link
                   </button>
                   <button onClick={(e) => { e.stopPropagation(); openLink(showcase.slug); }} className="w-10 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg flex items-center justify-center transition-colors">
                      <ExternalLink size={14} />
                   </button>
                </div>
             </div>
          ))}
       </div>
    </div>
  );
};
